import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import type { IIndexable } from 'donor-elf-web/lib/common-types';
import {
  type AppliedPropertyFilter,
  ContactIdsIntersection,
  deserializeAppliedFilters,
  type PropertyFilterInfoForQueryFilter,
  serializeAppliedFilters,
} from 'donor-elf-web/lib/filter-utils-new';
import objectUtils from 'donor-elf-web/lib/object-utils';
import { pledgeMonthlyFrequency } from 'donor-elf-web/lib/pledge-utils';
import ContactModel from 'donor-elf-web/models/contact';
import PledgeModel from 'donor-elf-web/models/pledge';
import AddSomethingModal, {
  type PledgeSavedArgs,
} from 'donor-elf-web/services/add-something-modal';
import CampaignService from 'donor-elf-web/services/campaign-service';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import ContactsExportService from 'donor-elf-web/services/contacts-export';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import FundsService from 'donor-elf-web/services/funds-service';
import Settings from 'donor-elf-web/services/settings';
import FileSaver from 'file-saver';
import moment from 'moment';
import Papa from 'papaparse';

const defaultSortBy = ['contact.nameLowerCase:asc'];

interface TableColumn {
  name: string;
  isFixed?: string;
  width?: number;
  isContactColumn?: boolean;
  isTotalColumn?: boolean;
  group?: GroupInterface;
  index?: number;
  sortBy: string[];
}

class TableRow {
  constructor(args: {
    contact: ContactModel;
    pledges: PledgeModel[];
    amounts: number[];
    defaultPledge: number;
    total: number;
  }) {
    this.contact = args.contact;
    this.pledges = args.pledges;
    this.amounts = args.amounts;
    this.defaultPledge = args.defaultPledge;
    this.total = args.total;
  }

  contact: ContactModel;
  @tracked pledges: PledgeModel[];
  amounts: number[];
  defaultPledge: number;
  total: number;
}

interface GroupInterface {
  year: string;
  month: string;
  label: string;
}

export default class DonationsByMonthController extends Controller {
  @service declare addSomethingModal: AddSomethingModal;
  @service declare campaignService: CampaignService;
  @service declare contactsCache: ContactsCache;
  @service declare contactsExport: ContactsExportService;
  @service declare formatDateService: FormatDateService;
  @service declare fundGoalService: FundGoalService;
  @service declare fundsService: FundsService;
  @service declare media: any;
  @service declare settings: Settings;
  @service declare store: Store;

  queryParams = ['filters', 'groupBy'];

  @tracked filters?: string;
  @tracked appliedFilters?: AppliedPropertyFilter[];

  @tracked loading = true;
  @tracked error?: any;
  @tracked rows: TableRow[] = [];
  @tracked tableColumns: TableColumn[] = [];

  @tracked groups?: GroupInterface[];
  @tracked totalRow?: any;
  savePledgeSubsciptionToken?: string;

  @tracked sortBy = defaultSortBy;
  @tracked groupBy = 'month';

  get sortedRows() {
    return sortByProperties(this.rows, this.sortBy);
  }

  ytdStartMonth: number = 0;

  @action
  templateDidInsert() {
    this.ytdStartMonth =
      this.fundGoalService.hasYtdGoalType &&
      this.fundGoalService.ytdStartMoment?.month() !== 0
        ? (this.fundGoalService.ytdStartMoment?.month() ?? 0)
        : 0;

    if (isEmpty(this.filters)) {
      this.filters = serializeAppliedFilters([
        this.defaultDateRangeAppliedFilter,
      ]);
    }
    this.deserializeFilters();
    this.load();

    if (this.savePledgeSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.savePledgeSubsciptionToken,
      );
    }
    this.savePledgeSubsciptionToken =
      this.addSomethingModal.subscribeToPledgeSaved(this.handlePledgeSaved);
  }

  @action
  templateWillDestroy() {
    this.loading = true;
    this.filters = undefined;
    this.appliedFilters = undefined;
    this.error = null;
    this.groups = undefined;
    this.rows = [];
    this.tableColumns = [];
    this.sortBy = defaultSortBy.slice();
    this.groupBy = 'month';
    if (this.savePledgeSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.savePledgeSubsciptionToken,
      );
    }
  }

  get defaultDateRangeAppliedFilter(): AppliedPropertyFilter {
    let from: moment.Moment;

    switch (this.groupBy) {
      case 'month':
        from =
          this.fundGoalService.hasYtdGoalType &&
          this.fundGoalService.ytdStartMoment
            ? this.fundGoalService.ytdStartMoment
            : moment().subtract(11, 'months').startOf('month');

        break;

      case 'year':
        from = moment().startOf('year').subtract(2, 'years');

        if (this.ytdStartMonth !== 0) {
          from.add(this.ytdStartMonth - 12, 'months');
        }

        break;

      default:
        throw new Error(`Unhandled groupBy value '${this.groupBy}'`);
    }

    const to = moment().endOf('month');

    return {
      propertyName: 'dateRange',
      operation: '><',
      val: [from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')],
    };
  }

  get dateRangeAppliedFilter() {
    let appliedFilter = this.appliedFilters?.find(
      (x) => x.propertyName === 'dateRange',
    )!;
    return {
      from: (appliedFilter.val as string[])[0],
      to: (appliedFilter.val as string[])[1],
    };
  }

  @cached
  get availablePropertyFilters(): PropertyFilterInfoForQueryFilter[] {
    let list: PropertyFilterInfoForQueryFilter[] = [];
    list.push({
      propertyName: 'dateRange',
      label: 'Date',
      propertyType: 'date',
      availableOperations: ['><'],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        if (appliedFilter.operation === '><') {
          let dates = appliedFilter.val as string[];
          queryFilter.from = dates[0];
          queryFilter.to = dates[1];
        }
      },
    });
    if (this.campaignService.campaignsEnabled) {
      list.push({
        propertyName: 'campaignId',
        label: 'Campaign',
        propertyType: 'list',
        availableOperations: ['any', 'absent', 'present'],
        listOptions: sortByProperties(this.campaignService.campaigns, [
          'name',
        ]).map((x) => {
          return { val: x.id, label: x.name };
        }),
        applyToQueryFilter: async (
          appliedFilter: AppliedPropertyFilter,
          queryFilter: any,
        ) => {
          if (appliedFilter.operation === 'any') {
            queryFilter.campaignIds = appliedFilter.val;
          } else if (appliedFilter.operation === 'absent') {
            queryFilter.campaignIds = 'absent';
          } else if (appliedFilter.operation === 'present') {
            queryFilter.campaignIds = 'present';
          }
        },
      });
    }
    let contactFilterInfos =
      this.contactsCache.availablePropertyFiltersForContacts.filter(
        (x) => x.custom.includeOnAnyReport && x.propertyName !== 'campaignIds',
      );
    for (let filterInfo of contactFilterInfos) {
      list.push({
        propertyName: filterInfo.propertyName,
        label: `Contact ${filterInfo.label}`,
        propertyType: filterInfo.propertyType,
        availableOperations: filterInfo.availableOperations,
        listOptions: filterInfo.listOptions,
        listIsSingleSelection: filterInfo.listIsSingleSelection,
        applyToQueryFilter: async (
          appliedFilter: AppliedPropertyFilter,
          queryFilter: any,
        ) => {
          let contactIds = (
            await this.contactsCache.filter([appliedFilter])
          ).map((x) => x.id);
          if (contactIds.length === 0) {
            contactIds = ['-1']; // since no records match the contact filter, force the api to return 0 records
          }
          queryFilter.contactIds = ContactIdsIntersection(
            queryFilter.contactIds,
            contactIds,
          );
        },
      });
    }
    return list;
  }

  @action
  async load() {
    try {
      this.loading = true;
      this.error = null;

      const { dateRangeAppliedFilter } = this;
      const queryFilter: any = {};

      const { availablePropertyFilters } = this;

      for (const appliedFilter of this.appliedFilters || []) {
        const propertyFilter = availablePropertyFilters.find(
          (x) => x.propertyName === appliedFilter.propertyName,
        )!;
        await propertyFilter.applyToQueryFilter(appliedFilter, queryFilter);
      }

      const pledgesFilter = {
        startDate: dateRangeAppliedFilter.from,
        endDate: dateRangeAppliedFilter.to,
      };

      const pledges = (
        await this.store.query('pledge', {
          filter: {
            ...objectUtils.decamelizeKeys(pledgesFilter),
            is_active: true,
          },
          sort: '-start_date',
        })
      ).slice();

      const pledgesByContactId: IIndexable<PledgeModel[]> = {};

      for (let pledge of pledges) {
        if (pledgesByContactId[pledge.contactId!] == null) {
          pledgesByContactId[pledge.contactId!] = [];
        }

        pledgesByContactId[pledge.contactId!]?.push(pledge);
      }

      const records = (
        await this.store.query('donations-by-month', {
          filter: objectUtils.decamelizeKeys(queryFilter),
        })
      ).slice();
      const byContactId: IIndexable<IIndexable<number>> = {};
      const totalByGroup: IIndexable<number> = { overall: 0 };
      const totalByContact: IIndexable<number> = {};
      const defaultPledgeByContact: IIndexable<{
        monthYear: number;
        amount: number;
      }> = {};

      for (const record of records) {
        const { contactId, year, month, amount } = record;

        if (this.contactsCache.getById(contactId)) {
          const groupKey = this.getGroupKey(year, month);

          byContactId[contactId] = byContactId[contactId] || {};
          byContactId[contactId][groupKey] =
            (byContactId[contactId][groupKey] || 0) + amount;
          totalByGroup[groupKey] = (totalByGroup[groupKey] || 0) + amount;
          totalByGroup['overall']! += amount;
          totalByContact[contactId] = (totalByContact[contactId] || 0) + amount;

          if (amount > 0) {
            const monthYear = year * 100 + month; // YYYYMM
            const defaultPledge = defaultPledgeByContact[contactId];

            if (defaultPledge == null || defaultPledge.monthYear < monthYear) {
              defaultPledgeByContact[contactId] = { monthYear, amount };
            } else {
              defaultPledge.amount += amount;
            }
          }
        }
      }

      const groups: GroupInterface[] = [];
      const to = moment(dateRangeAppliedFilter.to);
      let date = moment(dateRangeAppliedFilter.from);

      while (date < to) {
        let label;
        let nextDate;

        switch (this.groupBy) {
          case 'month':
            label = date.format('MMM YYYY');
            nextDate = moment(date).add(1, 'month');

            break;

          case 'year':
            nextDate = moment(date).add(1, 'year');

            if (this.ytdStartMonth !== 0) {
              label = `${date.format('MM/YYYY')} - ${moment(date).add(11, 'months').format('MM/YYYY')}`;
            } else {
              label = date.format('YYYY');
            }

            break;

          default:
            throw new Error(`Unhandled groupBy value '${this.groupBy}'`);
        }

        groups.push({
          year: date.format('YYYY'),
          month: date.format('M'),
          label,
        });

        date = nextDate;
      }

      this.groups = groups;
      this.totalRow = [
        {
          totalAmounts: groups.map((item) => {
            return totalByGroup[this.getGroupKey(item.year, item.month)];
          }),
          overallTotal: totalByGroup['overall'],
        },
      ];

      const rows: TableRow[] = [];
      for (let contactId in byContactId) {
        rows.push(
          new TableRow({
            contact: this.contactsCache.getById(contactId),
            pledges: pledgesByContactId[contactId]!,
            amounts: groups.map((item) => {
              return byContactId[contactId]![
                this.getGroupKey(item.year, item.month)
              ]!;
            }),
            defaultPledge: defaultPledgeByContact[contactId]?.amount ?? 0,
            total: totalByContact[contactId]!,
          }),
        );
      }

      this.rows = rows;
      this.loading = false;
      this.setTableColumns();
    } catch (error: any) {
      this.error = error;
    }
  }

  setTableColumns() {
    let tableColumns: TableColumn[] = [];
    tableColumns.push({
      name: 'Contact',
      isFixed: 'left',
      width: this.media.isSmallDown ? 100 : 200,
      isContactColumn: true,
      sortBy: defaultSortBy,
    });
    this.groups!.forEach((group: any, index: any) => {
      tableColumns.push({
        name: group.label,
        group,
        width: 125,
        index: index,
        sortBy: [`amounts.${index}`],
      });
    });
    tableColumns.push({
      name: 'Total',
      isTotalColumn: true,
      width: 125,
      sortBy: ['total'],
    });
    this.tableColumns = tableColumns;
  }

  @action
  handleFiltersChanged(currentFilters: AppliedPropertyFilter[]) {
    // only allow one dateRange filter
    if (
      currentFilters.filter((x) => x.propertyName === 'dateRange').length > 1
    ) {
      currentFilters = [
        currentFilters.find((x) => x.propertyName === 'dateRange')!,
        ...currentFilters.filter((x) => x.propertyName !== 'dateRange'),
      ];
    }

    // confirm a dateRange filter is present
    if (!currentFilters.some((x) => x.propertyName === 'dateRange')) {
      currentFilters.push(this.defaultDateRangeAppliedFilter);
    }

    // confirm dateRange 'from' date is always at the beginning of the month
    // and the 'to' date is always at the end of the month
    let dateRangeFilter = currentFilters.find(
      (x) => x.propertyName === 'dateRange',
    )!;
    let dateRangeVals = dateRangeFilter.val as string[];
    dateRangeFilter.val = [
      moment(dateRangeVals[0]).startOf('month').format('YYYY-MM-DD'),
      moment(dateRangeVals[1]).endOf('month').format('YYYY-MM-DD'),
    ];

    this.appliedFilters = currentFilters;
    this.serializeFilters();
    this.load();
  }

  serializeFilters() {
    this.filters = serializeAppliedFilters(this.appliedFilters);
  }

  deserializeFilters() {
    this.appliedFilters = deserializeAppliedFilters(this.filters);
  }

  get actionsMenuItems() {
    return [
      {
        label: 'Download',
        svgJar: 'download',
        onClick: this.download,
      },
    ];
  }

  @action
  applySortByColumn(column: any) {
    if (this.sortBy.join(',') === column.sortBy.join(',')) {
      this.sortBy[0] = `${this.sortBy[0]}:desc`;
      this.sortBy = [...this.sortBy];
    } else {
      this.sortBy = column.sortBy.slice();
    }
  }

  @action
  addPledge(row: TableRow) {
    this.addSomethingModal.open({
      type: 'pledge',
      pledgeOptions: {
        contactId: row.contact.id,
        amount: row.defaultPledge,
        frequency: pledgeMonthlyFrequency,
      },
    });
  }

  @action
  async handlePledgeSaved(_msg: string, data: PledgeSavedArgs) {
    if (data.newPledge) {
      let row = this.rows.find((x) => x.contact.id === data.pledge.contactId);
      if (row) {
        row.pledges = [...(row.pledges || []), data.pledge];
      }
    }
  }

  @action
  async download() {
    let rows: any[] = [];
    let headerRow = ['Name'];
    this.groups!.forEach((group: any) => {
      headerRow.push(group.label);
    });
    headerRow.push('Total');
    headerRow.push('---');
    let headerInfo = await this.contactsExport.addContactsHeaderColumns(
      headerRow,
      this.contactsCache.contacts,
    );
    rows.push(headerRow);
    for (let item of this.sortedRows) {
      let row: any[] = [item.contact?.name];
      for (let i = 0; i < this.groups!.length; i++) {
        row.push(item.amounts[i]);
      }
      row.push(item.total);
      row.push('');
      await this.contactsExport.addContactInfoToRow(
        row,
        item.contact,
        headerInfo,
      );
      rows.push(row);
    }
    rows.push([]);
    let csvTotalRow = ['Total'];
    this.totalRow[0].totalAmounts.forEach((total: any) => {
      csvTotalRow.push(total);
    });
    csvTotalRow.push(this.totalRow[0].overallTotal);
    rows.push(csvTotalRow);

    this.store.createRecord('contact-download').save();

    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, `donations-by-${this.groupBy}.csv`, true);
  }

  @action
  adjustTableContainerHeight(el: HTMLElement) {
    let newHeight =
      window.innerHeight -
      (el.getBoundingClientRect().top + window.scrollY) -
      20;
    el.style.height = `${newHeight}px`;
  }

  handleTabClick = (tab: string) => {
    this.groupBy = tab;
    this.appliedFilters =
      this.appliedFilters?.filter((f) => f.propertyName !== 'dateRange') || [];
    this.handleFiltersChanged(this.appliedFilters);
  };

  getGroupKey(year: number | string, month: number | string): string {
    if (typeof year === 'string') {
      year = parseInt(year);
    }

    if (typeof month === 'string') {
      month = parseInt(month);
    }

    switch (this.groupBy) {
      case 'month':
        return `${year}-${month}`;

      case 'year':
        const startMonth = this.ytdStartMonth;

        if (startMonth !== 0 && month <= startMonth) {
          year--;
        }

        return year.toString();

      default:
        throw new Error(`Unhandled groupBy value '${this.groupBy}'`);
    }
  }
}
